import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const {
    FiPlus, FiEdit3, FiTrash2, FiMaximize, FiMinimize,
    FiSearch, FiDollarSign, FiTarget, FiX, FiPieChart
} = FiIcons;

const Expense = () => {
    const { user } = useAuth();
    const [expenses, setExpenses] = useState([]);
    const [budget, setBudget] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [filterFrequency, setFilterFrequency] = useState('');
    const [editingExpense, setEditingExpense] = useState(null);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [isSettingBudget, setIsSettingBudget] = useState(false);

    const userId = user?.id || 'demo-user-123';

    useEffect(() => {
        loadData();
    }, [userId]);

    const loadData = async () => {
        setLoading(true);
        try {
            // const [expenseData, budgetData] = await Promise.all([
            //     api.getExpenses(userId),
            //     api.getBudget(userId)
            // ]);

            const [expenseData] = await Promise.all([
                api.get('expenses'),
            ]);
            setExpenses(expenseData.data.data || []);
            setBudget(localStorage.getItem(`budget_${userId}`) || 0);
        } catch (error) {
            toast.error('Failed to load data');
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const normalizeToMonthly = (amount, frequency) => {
        const val = parseFloat(amount || 0);
        switch (frequency) {
            case 'Daily': return val * 30;
            case 'Weekly': return val * 4.33;
            case 'Bi-weekly': return val * 2.16;
            case 'Monthly': return val;
            case 'Quarterly': return val / 3;
            case 'Bi-Annually': return val / 6;
            case 'Annually': return val / 12;
            default: return val;
        }
    };

    const payment_frequencies = ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Bi-Annually', 'Annually'];

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount || 0);
    };

    // Calculate total monthly normalized expenses (Consistent with Dashboard)
    const totalMonthlyAmount = React.useMemo(() => {
        return expenses.reduce((sum, expense) =>
            sum + normalizeToMonthly(expense.amount, expense.payment_frequency), 0
        );
    }, [expenses]);

    const sortedExpenses = React.useMemo(() => {
        let sortableExpenses = [...expenses];
        if (searchTerm) {
            sortableExpenses = sortableExpenses.filter(expense =>
                expense.service?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                expense.comments?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        if (filterFrequency) {
            sortableExpenses = sortableExpenses.filter(expense =>
                expense.payment_frequency === filterFrequency
            );
        }
        if (sortConfig.key) {
            sortableExpenses.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];
                if (sortConfig.key === 'amount') {
                    aValue = parseFloat(aValue);
                    bValue = parseFloat(bValue);
                }
                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableExpenses;
    }, [expenses, searchTerm, filterFrequency, sortConfig]);

    const overBudget = budget > 0 && totalMonthlyAmount > budget;
    const budgetProgress = budget > 0 ? Math.min((totalMonthlyAmount / budget) * 100, 100) : 0;

    // Filtered total normalized calculation
    const filteredTotalAmount = React.useMemo(() => {
        return sortedExpenses.reduce((sum, expense) =>
            sum + normalizeToMonthly(expense.amount, expense.payment_frequency), 0
        );
    }, [sortedExpenses]);

    const isFiltered = searchTerm || filterFrequency;

    const handleSaveExpense = async (expenseData) => {
        try {
            if (expenseData.id) {
                await api.put(`expenses/${expenseData.id}`, expenseData);
            } else {
                await api.post('expenses', expenseData);
            }

            const response = await api.get('expenses');
            setExpenses(response.data.data || []);

            setIsAddingNew(false);
            setEditingExpense(null);

            toast.success(expenseData.id ? 'Expense updated' : 'Expense added');
        } catch (error) {
            toast.error('Failed to save expense');
            console.log(error);
        }
    };

    const handleSaveBudget = async (newBudget) => {
        try {
            setBudget(Number(newBudget));
            localStorage.setItem(`budget_${userId}`, newBudget);
            setIsSettingBudget(false);
            toast.success('Budget updated successfully');
        } catch (error) {
            toast.error('Failed to update budget');
            console.log(error);
        }
    };

    const handleDeleteExpense = async (id) => {
        if (window.confirm('Delete this expense?')) {
            try {
                await api.delete(`/expenses/${id}`);
                const response = await api.get('expenses');
                setExpenses(response.data.data || []);
                toast.success('Expense deleted');
            } catch (error) {
                toast.error('Delete failed');
                console.log(error);
            }
        }
    };

    if (loading) return <div className="flex items-center justify-center min-h-screen text-gray-500">Loading expenses...</div>;

    return (
        <div className={`${isFullscreen ? 'fixed inset-0 z-50 bg-white dark:bg-gray-900' : 'min-h-screen bg-gray-50 dark:bg-gray-900'} transition-all duration-300`}>
            <div className={`${isFullscreen ? 'p-4' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'}`}>
                {/* Header */}
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 space-y-4 lg:space-y-0">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Expense Tracker</h1>
                        <p className="text-gray-600 dark:text-gray-300">Manage recurring expenses and track your budget</p>
                    </div>
                    <div className="flex items-center space-x-4">
                        <div className="flex flex-col items-end">
                            <div className={`bg-white dark:bg-gray-800 rounded-lg px-4 py-2 shadow-sm border ${overBudget ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'}`}>
                                <div className="flex items-center space-x-2">
                                    <SafeIcon icon={FiDollarSign} className={`w-5 h-5 ${overBudget ? 'text-red-600' : 'text-green-600'}`} />
                                    <span className={`text-lg font-bold ${overBudget ? 'text-red-600' : 'text-gray-900 dark:text-white'}`}>{formatCurrency(totalMonthlyAmount)}</span>
                                    <span className="text-sm text-gray-400 font-normal">/ month</span>
                                </div>
                            </div>
                            {budget > 0 && (
                                <div className="mt-2 w-48">
                                    <div className="flex justify-between text-[10px] font-bold text-gray-400 uppercase tracking-tighter mb-1">
                                        <span>Budget Progress</span>
                                        <span>{Math.round((totalMonthlyAmount / budget) * 100)}%</span>
                                    </div>
                                    <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: `${budgetProgress}%` }}
                                            className={`h-full ${overBudget ? 'bg-red-500' : 'bg-primary-500'}`}
                                        />
                                    </div>
                                    <div className="flex justify-end mt-1">
                                        <span className="text-[10px] font-medium text-gray-500">Target: {formatCurrency(budget)}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        <button onClick={() => setIsFullscreen(!isFullscreen)} className="p-2 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                            <SafeIcon icon={isFullscreen ? FiMinimize : FiMaximize} className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Controls */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
                    <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                        <div className="flex flex-col sm:flex-row gap-4 flex-1">
                            <div className="relative">
                                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input type="text" placeholder="Search expenses..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full sm:w-64 outline-none focus:ring-2 focus:ring-primary-500/20" />
                            </div>
                            <select value={filterFrequency} onChange={(e) => setFilterFrequency(e.target.value)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary-500/20 cursor-pointer">
                                <option value="">All Frequencies</option>
                                {payment_frequencies.map(f => <option key={f} value={f}>{f}</option>)}
                            </select>
                        </div>
                        <div className="flex items-center space-x-3 w-full lg:w-auto">
                            <button
                                onClick={() => setIsSettingBudget(true)}
                                className="flex items-center space-x-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 font-medium transition-all w-full lg:w-auto justify-center shadow-sm"
                            >
                                <SafeIcon icon={FiTarget} className="w-4 h-4 text-primary-500" />
                                <span>Set Budget</span>
                            </button>
                            <button onClick={() => setIsAddingNew(true)} className="flex items-center space-x-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition-all w-full lg:w-auto justify-center shadow-md shadow-primary-500/20">
                                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                                <span>Add Expense</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Table Container */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-gray-700/50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Service</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Frequency</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Day</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Last Paid</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                                        <div className="flex flex-col">
                                            {isFiltered && (
                                                <span className="text-[10px] font-bold text-primary-600 dark:text-primary-400 mb-0.5 animate-pulse leading-tight">
                                                    Filtered Monthly: {formatCurrency(filteredTotalAmount)}
                                                </span>
                                            )}
                                            <span>Amount</span>
                                        </div>
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {sortedExpenses.length > 0 ? (
                                    sortedExpenses.map((expense) => (
                                        <tr key={expense.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors group">
                                            <td className="px-6 py-2 whitespace-nowrap">
                                                <div className="font-semibold text-gray-900 dark:text-white text-sm">{expense.service}</div>
                                            </td>
                                            <td className="px-6 py-2 whitespace-nowrap text-xs text-gray-600 dark:text-gray-400">{expense.payment_frequency}</td>
                                            <td className="px-6 py-2 whitespace-nowrap text-xs text-gray-600 dark:text-gray-400 font-medium">{expense.day_of_month_paid}</td>
                                            <td className="px-6 py-2 whitespace-nowrap text-xs text-gray-600 dark:text-gray-400">{expense.last_paid_date ? format(new Date(expense.last_paid_date), 'MMM dd, yyyy') : 'N/A'}</td>
                                            <td className="px-6 py-2 whitespace-nowrap text-sm font-bold text-gray-900 dark:text-white">{formatCurrency(expense.amount)}</td>
                                            <td className="px-6 py-2 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex justify-end space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button onClick={() => setEditingExpense(expense)} className="p-1 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-md transition-colors"><SafeIcon icon={FiEdit3} className="w-3.5 h-3.5" /></button>
                                                    <button onClick={() => handleDeleteExpense(expense.id)} className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"><SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-10 text-center text-gray-500 dark:text-gray-400">
                                            <div className="flex flex-col items-center">
                                                <SafeIcon icon={FiPieChart} className="w-10 h-10 text-gray-300 mb-3" />
                                                <p className="text-sm">No expenses found. Add your first recurring payment!</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <AnimatePresence>
                    {(isAddingNew || editingExpense) && (
                        <ExpenseModal
                            expense={editingExpense}
                            isOpen={true}
                            onClose={() => { setIsAddingNew(false); setEditingExpense(null); }}
                            onSave={handleSaveExpense}
                            payment_frequencies={payment_frequencies}
                        />
                    )}

                    {isSettingBudget && (
                        <BudgetModal
                            currentBudget={budget}
                            onClose={() => setIsSettingBudget(false)}
                            onSave={handleSaveBudget}
                        />
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

const BudgetModal = ({ currentBudget, onClose, onSave }) => {
    const [budgetVal, setBudgetVal] = useState(currentBudget || '');

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(budgetVal);
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-sm w-full p-8 border border-gray-200 dark:border-gray-700 relative overflow-hidden"
            >
                <div className="absolute top-0 left-0 w-full h-1.5 bg-primary-600"></div>
                <button onClick={onClose} className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg">
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>

                <div className="flex flex-col items-center text-center mb-6">
                    <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center text-primary-600 dark:text-primary-400 mb-4">
                        <SafeIcon icon={FiTarget} className="w-6 h-6" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">Monthly Budget</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Set a target amount to track your spending capacity</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Amount (USD)</label>
                        <div className="relative">
                            <SafeIcon icon={FiDollarSign} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="number"
                                step="0.01"
                                placeholder="0.00"
                                value={budgetVal}
                                onChange={(e) => setBudgetVal(e.target.value)}
                                className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-lg font-bold text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary-500/20 transition-all"
                                autoFocus
                                required
                            />
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <button type="button" onClick={onClose} className="flex-1 py-3 text-gray-500 dark:text-gray-400 font-bold text-xs uppercase tracking-widest hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all">Cancel</button>
                        <button type="submit" className="flex-1 py-3 bg-primary-600 text-white font-bold text-xs uppercase tracking-widest rounded-xl hover:bg-primary-700 shadow-lg shadow-primary-500/20 transition-all">Set Budget</button>
                    </div>
                </form>
            </motion.div>
        </div>
    );
};

const ExpenseModal = ({ expense, isOpen, onClose, onSave, payment_frequencies }) => {
    const [formData, setFormData] = useState({ service: '', payment_frequency: 'Monthly', day_of_month_paid: 1, last_paid_date: format(new Date(), 'yyyy-MM-dd'), amount: '', comments: '' });
    useEffect(() => { if (expense) setFormData(expense); }, [expense]);
    const handleSubmit = (e) => { e.preventDefault(); onSave(formData); };
    
    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-md w-full p-8 border border-gray-200 dark:border-gray-700 relative">
                <button onClick={onClose} className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg transition-colors">
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg mr-3">
                        <SafeIcon icon={FiPlus} className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    {expense ? 'Modify' : 'New'} Recurring Expense
                </h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Service Name</label>
                        <input type="text" placeholder="e.g. Netflix, Rent, AWS" value={formData.service} onChange={(e) => setFormData({ ...formData, service: e.target.value })} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 outline-none transition-all" required />
                    </div>
                    <div className="grid grid-cols-2 gap-5">
                        <div className="space-y-1.5">
                            <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Frequency</label>
                            <select value={formData.payment_frequency} onChange={(e) => setFormData({ ...formData, payment_frequency: e.target.value })} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 outline-none cursor-pointer">
                                {payment_frequencies.map(f => <option key={f} value={f}>{f}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Payment Day</label>
                            <input type="number" min="1" max="31" value={formData.day_of_month_paid} onChange={(e) => setFormData({ ...formData, day_of_month_paid: e.target.value })} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 outline-none" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-5">
                        <div className="space-y-1.5">
                            <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Last Paid Date</label>
                            <input type="date" value={formData.last_paid_date ? formData.last_paid_date.split('T')[0] : ''} onChange={(e) => setFormData({ ...formData, last_paid_date: e.target.value })} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 outline-none" />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Amount ($)</label>
                            <input type="number" step="0.01" placeholder="0.00" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 outline-none font-bold" required />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Notes (Optional)</label>
                        <textarea value={formData.comments} onChange={(e) => setFormData({ ...formData, comments: e.target.value })} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 outline-none resize-none" rows="2"></textarea>
                    </div>
                    <div className="flex justify-end space-x-3 mt-8 pt-4">
                        <button type="button" onClick={onClose} className="px-6 py-2.5 text-gray-500 dark:text-gray-400 font-bold text-xs uppercase tracking-widest hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all">Cancel</button>
                        <button type="submit" className="px-8 py-2.5 bg-primary-600 text-white font-bold text-xs uppercase tracking-widest rounded-xl hover:bg-primary-700 shadow-lg shadow-primary-500/20 transition-all">{expense ? 'Update Record' : 'Add Record'}</button>
                    </div>
                </form>
            </motion.div>
        </div>
    );
};

export default Expense;